import React, { useEffect, useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Tooltip } from 'primereact/tooltip';
import type { PeriodicReport, PeriodicReportErrorCode } from '@brand24-original/llm-types';
import './PeriodicReportsPage.scss';
import Loader from '../../shared/components/common/loader/Loader';
import { PeriodicReportService } from '../../shared/services/integrations/PeriodicReportService';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { DataTable } from '../../shared/components/common/data-table/DataTable';
import { Column } from 'primereact/column';
import SimpleBar from 'simplebar-react';
import { Paginator } from 'primereact/paginator';
import ColorTag from '../../shared/components/color-tag/ColorTag';
import { keywordsTemplate } from '../../shared/helpers/ColumnHelper';
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import ReportSetupDialog from './components/setup-dialog/ReportSetupDialog';
import { format } from 'date-fns';
import { getProjectsFromAccount } from '../../shared/helpers/project/ProjectHelper';
import { useTranslation } from 'react-i18next';
import Title from '../../shared/components/title/Title';
import MoreBtn from '../../shared/components/common/more-btn/MoreBtn';
import { LookerStudioService } from '../../shared/services/integrations/LookerStudioService';
import EmptyState from '../../shared/components/common/empty-state/EmptyState';
import { INTEGRATIONS, PROJECTS } from '../../shared/routes/path';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';
import { MOBILE_PAGINATOR_TEMPLATE } from '../../shared/utils/mobile';
import {
  currentLookerParameters,
  getBuildStateColor,
  getBuildStateName,
  getDatasetName,
  getHealthColor,
  getHealthName,
  getRecomputeBlockReason,
  getStatusName,
  getStatusColor,
  getTypeName,
  isLegacyLookerReport,
} from '../../shared/helpers/periodic-reports/PeriodicReportsHelper';

/** While a report builds, the list re-reads itself this often (silently — no loader). */
const BUILDING_POLL_MS = 30_000;

const PeriodicReportsPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { accountResponse, projectResponse } = useAccount();
  const [reports, setReports] = useState<PeriodicReport[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<{[key: number]: boolean}>({});
  // Sortowanie i strony liczone tutaj: paginator stoi poza tabelą (pod SimpleBarem), więc DataTable
  // dostaje już gotowy wycinek.
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(50);
  const [sortField, setSortField] = useState<string | null>('created');
  const [sortOrder, setSortOrder] = useState<1 | -1 | 0>(-1);

  // Okno zakładania raportu: `report: null` = krok 1 (nowy), raport = krok 2 (czeka na link).
  const [setup, setSetup] = useState<{ visible: boolean; report: PeriodicReport | null }>({
    visible: false,
    report: null,
  });

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      const data = await PeriodicReportService.getPeriodicReports();
      setReports(data);
      setError(null);
    } catch (err) {
      setError(t('failedToLoadPeriodicReports'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  // A report that is building fills its sheet within minutes; re-read the list in the background so its
  // progress and state update — never while the setup dialog is open (the list swap would close it).
  const isBuilding = !!reports?.some((report) => currentLookerParameters(report)?.build.state === 'BUILDING');
  useEffect(() => {
    if (!isBuilding || setup.visible) return undefined;
    const timer = setInterval(() => {
      PeriodicReportService.getPeriodicReports()
        .then(setReports)
        .catch(() => undefined);
    }, BUILDING_POLL_MS);
    return () => clearInterval(timer);
  }, [isBuilding, setup.visible]);

  // Lista odświeża się dopiero przy zamknięciu — `loadReports` podmienia stronę na loader, a to
  // zamknęłoby okno w połowie zakładania raportu.
  const hideSetupDialog = () => {
    setSetup((current) => ({ ...current, visible: false }));
    loadReports();
  };

  const openCreateReportDialog = () => setSetup({ visible: true, report: null });

  const openReportLinkDialog = (report: PeriodicReport) => setSetup({ visible: true, report });

  const confirmDelete = (report: PeriodicReport) => {
    confirmDialog({
      message: t('confirmDeletePeriodicReport'),
      header: t('confirmDeletion'),
      defaultFocus: 'accept',
      accept: () => deleteReport(report),
    });
  };

  const confirmToggleStatus = (report: PeriodicReport) => {
    const action = report.status === 'ACTIVE' ? t('deactivate') : t('activate');
    confirmDialog({
      message: `${t('areYouSureYouWantTo')} ${action} ${t('thisPeriodicReport')}?`,
      header: `${t('confirm')} ${action.charAt(0).toUpperCase() + action.slice(1)}`,
      defaultFocus: 'accept',
      accept: () => toggleReportStatus(report),
    });
  };

  const deleteReport = async (report: PeriodicReport) => {
    setActionLoading(prev => ({ ...prev, [report.id]: true }));
    try {
      const success = await PeriodicReportService.deletePeriodicReport(report.id);
              if (success) {
          await loadReports(); // Refresh the list
        } else {
          setError(t('failedToDeleteReport'));
        }
    } catch (err) {
      setError(t('failedToDeleteReportTryAgain'));
    } finally {
      setActionLoading(prev => ({ ...prev, [report.id]: false }));
    }
  };

  const toggleReportStatus = async (report: PeriodicReport) => {
    setActionLoading(prev => ({ ...prev, [report.id]: true }));
    try {
      if (report.status === 'ACTIVE') {
        await PeriodicReportService.deactivatePeriodicReport(report.id);
      } else {
        await PeriodicReportService.activatePeriodicReport(report.id);
      }
      await loadReports(); // Refresh the list
    } catch (err) {
      setError(t('failedToUpdateReportStatus'));
    } finally {
      setActionLoading(prev => ({ ...prev, [report.id]: false }));
    }
  };

  const confirmRecompute = (report: PeriodicReport) => {
    confirmDialog({
      message: t('confirmRecomputeLookerReport'),
      header: t('lookerRecomputeAll'),
      defaultFocus: 'accept',
      accept: () => recomputeReport(report),
    });
  };

  // The row is patched with the answer — no `loadReports()`, which would swap the page for the loader.
  const recomputeReport = async (report: PeriodicReport) => {
    setActionLoading(prev => ({ ...prev, [report.id]: true }));
    try {
      const queued = await PeriodicReportService.recomputePeriodicReport(report.id);
      setReports((current) => current?.map((item) => (item.id === queued.id ? queued : item)) ?? current);
    } catch (err: any) {
      const code: PeriodicReportErrorCode | undefined = err?.response?.data?.code;
      setError(
        t(
          code === 'RECOMPUTE_LIMIT'
            ? 'lookerRecomputeBlockedRecent'
            : code === 'RECOMPUTE_IN_PROGRESS'
              ? 'lookerRecomputeBlockedBuilding'
              : 'failedToRecomputeReport'
        )
      );
    } finally {
      setActionLoading(prev => ({ ...prev, [report.id]: false }));
    }
  };

  const getProjectName = (projectId: number) => {
    const filteredProjects = getProjectsFromAccount(accountResponse, projectResponse || []);
    const project = filteredProjects.find((p: any) => p.id === projectId);
    return project ? project.projectLabel : `Project ${projectId}`;
  };



  const getReportName = (report: PeriodicReport) => {
    if (report.config?.parameters?.reportName) {
      return report.config.parameters.reportName;
    }
    return `${getProjectName(report.projectId)} - ${getTypeName(report.type as string)} ${t('report')}`;
  };

  const tryOpenReport = async (reportId: number): Promise<{ success: boolean; url?: string; needsModal: boolean }> => {
    try {
      const resolved = await LookerStudioService.getReportUrl(reportId);
      
      if (resolved?.url) {
        if (resolved.status === 'exists') {
          return { success: true, url: resolved.url, needsModal: false };
        } else if (resolved.status === 'create') {
          return { success: true, needsModal: true };
        }
      }
      
      return { success: false, needsModal: false };
    } catch (error) {
      return { success: false, needsModal: false };
    }
  };

  const showReportModal = (report: PeriodicReport) => {
    openReportLinkDialog(report);
  };

  const openReportInNewWindow = async (report: PeriodicReport) => {
    try {
      // First, check if report is already active based on local data
      if (report.status === 'ACTIVE' && report.config?.parameters?.finalReportUrl) {
        // Report is active and has final URL - open directly
        window.open(report.config.parameters.finalReportUrl, '_blank', 'noopener,noreferrer');
        return;
      }

      // If not active locally, try to get current status from backend
      const result = await tryOpenReport(report.id);
      
      if (result.success) {
        if (result.url) {
          // Report exists - open directly
          window.open(result.url, '_blank', 'noopener,noreferrer');
          // Refresh table to show updated status
          await loadReports();
          return;
        } else if (result.needsModal) {
          // Need to create report - show modal
          showReportModal(report);
          return;
        }
      }

      // If backend check failed, refresh data and check status again
      await loadReports();
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const updatedReport = reports?.find(r => r.id === report.id);
      if (!updatedReport) {
        setError(t('reportNotFound'));
        return;
      }

      // Check if status changed to PENDING or if we need to show modal
      if (updatedReport.status === 'PENDING') {
        showReportModal(updatedReport);
      } else {
        // Try one more time with updated data
        const finalResult = await tryOpenReport(updatedReport.id);
        
        if (finalResult.success && finalResult.url) {
          window.open(finalResult.url, '_blank', 'noopener,noreferrer');
          await loadReports();
        } else {
          showReportModal(updatedReport);
        }
      }
    } catch (error) {
      // Fallback to stored URL if available
      const reportUrl = report.config?.parameters?.finalReportUrl || report.config?.parameters?.reportUrl;
      if (reportUrl) {
        window.open(reportUrl, '_blank', 'noopener,noreferrer');
      } else {
        setError(t('failedToOpenReport'));
      }
    }
  };

  const actionBodyTemplate = (report: PeriodicReport) => {
    const isLoading = actionLoading[report.id];
    const current = currentLookerParameters(report);
    const recomputeBlock = current ? getRecomputeBlockReason(current) : null;
    
    return (
      <div className="buttons-list">
        {/* Toggle Status Button — nie dla raportu bez linku: „Activate” ominęłoby dokończenie konfiguracji. */}
        {report.status !== 'PENDING' && (
          <Button
            className="action-btn"
            onClick={() => confirmToggleStatus(report)}
            disabled={isLoading}
            title={report.status === 'ACTIVE' ? t('deactivate') : t('activate')}
          >
            {isLoading ? (
              <span className="loading-dots">...</span>
            ) : (
              <span className={`ico--${report.status === 'ACTIVE' ? 'pause' : 'run'} ico--xs`} />
            )}
          </Button>
        )}
        
        {/* Link do raportu — wklejenie pominięte przy zakładaniu albo zmiana zapisanego adresu.
            Nie dla wstrzymanych: zapis linku ustawia raport jako aktywny. */}
        {report.provider === 'LOOKER_STUDIO' && ['PENDING', 'ACTIVE'].includes(report.status) && (
          <Button
            className="action-btn"
            onClick={() => openReportLinkDialog(report)}
            disabled={isLoading}
            title={t('lookerSetupLinkAction')}
          >
            <span className="ico--edit ico--xs" />
          </Button>
        )}

        {/* "Recompute all data" — current-format reports only, once per 24 h (the backend's 409/429 rule). */}
        {current && report.status !== 'PENDING' && (
          <Button
            className="action-btn"
            onClick={() => confirmRecompute(report)}
            disabled={isLoading || !!recomputeBlock}
            title={
              recomputeBlock === 'building'
                ? t('lookerRecomputeBlockedBuilding')
                : recomputeBlock === 'recent'
                  ? t('lookerRecomputeBlockedRecent')
                  : t('lookerRecomputeAll')
            }
          >
            <span className="ico--refresh ico--xs" />
          </Button>
        )}

        {/* Delete Button */}
        <Button
          className="action-btn"
          onClick={() => confirmDelete(report)}
          disabled={isLoading}
          title={t('delete')}
        >
          {isLoading ? (
            <span className="loading-dots">...</span>
          ) : (
            <span className="ico--trash ico--xs" />
          )}
        </Button>
      </div>
    );
  };

  const createReportButton = (
    <button type="button" className="btn btn--sm" onClick={openCreateReportDialog}>
      <span className="ico--add ico--xs" aria-hidden="true" />
      {t('createReport')}
    </button>
  );

  // The export is the LOOKER_STUDIO addon's — the backend refuses creating without it, and the nightly
  // job stops refreshing; the list is not reachable either.
  if (accountResponse && !accountResponse.addons.includes('LOOKER_STUDIO')) {
    return <Navigate to={PROJECTS} replace />;
  }

  if (loading) {
    return (
      <div className="periodic-reports-page">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="periodic-reports-page">
        <div className="error-message">
          <p>{error}</p>
          <Button label={t('retry')} onClick={loadReports} />
        </div>
      </div>
    );
  }

  // Kolumny nazwy i projektu sortują się po tym, co widać, a nie po id.
  const sortValue = (report: PeriodicReport): string => {
    if (sortField === 'id') return getReportName(report).toLowerCase();
    if (sortField === 'projectId') return getProjectName(report.projectId).toLowerCase();
    if (sortField === 'lastExecuted') return currentLookerParameters(report)?.lastRefreshAt ?? report.lastExecuted ?? '';
    return String(report[sortField as keyof PeriodicReport] ?? '').toLowerCase();
  };
  const sortedReports =
    !reports || !sortField || !sortOrder
      ? reports ?? []
      : [...reports].sort((a, b) => sortValue(a).localeCompare(sortValue(b)) * sortOrder);
  // Usunięcie ostatniego raportu ze strony skraca listę — bez tego zostałaby pusta strona za końcem.
  const pageFirst =
    first < sortedReports.length ? first : Math.max(0, Math.floor((sortedReports.length - 1) / rows) * rows);

  return (
    <div className="page-content periodic-reports-route">
      <div className="page-box">
        {/* Telefon: tytuł i „Utwórz raport” (sama ikona) w górnym pasku — przy pustej liście przycisk jest
            w pustym stanie, więc w pasku go wtedy nie ma. */}
        <MobilePageHeader
          title={<span>{t('periodicReports')}</span>}
          actions={
            reports &&
            reports.length > 0 && (
              <button
                type="button"
                className="btn btn--sm mobile-header__add"
                onClick={openCreateReportDialog}
                aria-label={t('createReport')}
              >
                <span className="ico--add ico--xs" aria-hidden="true" />
              </button>
            )
          }
        />
        {!isMobile && (
        // Ten sam układ co lista raportów widoczności: przyklejony nagłówek + `page-box__stats` z tabelą.
        <div className="page-box__filters" data-observe-sticky>
          <Title
            title={t('periodicReports')}
            isBaseCategory={false}
            breadcrumbs={[{ name: t('integrations'), href: INTEGRATIONS }, { name: t('lookerStudio') }]}
            // Przy pustej liście przycisk jest już w pustym stanie — nie dublujemy go w nagłówku.
            children={
              reports && reports.length > 0 ? (
                <div className="page-box__buttons">{createReportButton}</div>
              ) : undefined
            }
          />
        </div>
        )}

        <div className="page-box__stats">
          <div className="periodic-reports-page">

            {error && (
              <div className="error-banner">
                <p>{error}</p>
                <Button 
                  label="×" 
                  className="error-banner__close"
                  onClick={() => setError(null)}
                />
              </div>
            )}

            {reports && reports.length > 0 ? (
              // Ten sam układ co lista projektów: SimpleBar właścicielem przewijania (spójny pasek w każdej
              // przeglądarce), a `scrollable` jest warunkiem działania zamrożonej kolumny akcji.
              <div className="periodic-reports-page__table table-content--simplebar">
                <div className="accordion-table-frame">
                <SimpleBar style={isMobile ? undefined : { maxHeight: '100vh' }} autoHide={false}>
                <DataTable
                  // `lazy`: tabela nie sortuje sama — inaczej kolumny nazwy i projektu szłyby po id.
                  lazy
                  stripedRows
                  scrollable
                  removableSort
                  sortField={sortField ?? undefined}
                  sortOrder={sortOrder}
                  onSort={(e) => {
                    setSortField(e.sortField || null);
                    setSortOrder((e.sortOrder as 1 | -1 | 0) ?? 0);
                    setFirst(0);
                  }}
                  value={sortedReports.slice(pageFirst, pageFirst + rows)}
                  dataKey="id"
                  tableStyle={{ minWidth: '50rem' }}
                  tableClassName="table"
                  className="table-wrapper table-wrapper--accordion table-wrapper--stack periodic-reports-page__cards"
                  responsiveLayout="stack"
                >
          <Column
            field="id"
            header={t('reportName')}
            sortable
            headerClassName="table__header"
            bodyClassName="table__cell--stack-title"
            body={(report) => (
              <div className="periodic-reports-page__name">
                <span className="ico--report ico--sm" />
                {/* Nazwa otwiera raport (albo konfigurację, gdy linku jeszcze nie ma) — jak nazwa projektu. */}
                {report.config?.parameters?.finalReportUrl ? (
                  <a href={report.config.parameters.finalReportUrl} target="_blank" rel="noopener noreferrer">
                    {getReportName(report)}
                  </a>
                ) : (
                  <button
                    type="button"
                    className="periodic-reports-page__name-btn"
                    onClick={() => openReportInNewWindow(report)}
                  >
                    {getReportName(report)}
                  </button>
                )}
                {report.provider === 'LOOKER_STUDIO' && report.status === 'PENDING' && (
                  <>
                    <span
                      id={`looker-setup-${report.id}`}
                      className="ico--warning ico--xs periodic-reports-page__setup-warning"
                    />
                    {/* autoHide={false}: kursor może zjechać na tooltip i kliknąć „Dokończ konfigurację”;
                        hideDelay daje mu czas na przejście przez odstęp między ikoną a tooltipem.
                        Przy otwartym oknie tooltip jest odmontowany — hide() nie zamknie go pod kursorem. */}
                    {!setup.visible && (
                    <Tooltip
                      target={`#looker-setup-${report.id}`}
                      position="top"
                      autoHide={false}
                      hideDelay={200}
                    >
                      <div className="periodic-reports-page__setup-tooltip">
                        {t('lookerSetupMissingLink')}
                        <button
                          type="button"
                          className="periodic-reports-page__finish"
                          onClick={() => openReportLinkDialog(report)}
                        >
                          {t('lookerSetupFinish')}
                        </button>
                      </div>
                    </Tooltip>
                    )}
                  </>
                )}
              </div>
            )}
            style={{ minWidth: '180px' }}
          />
          <Column
            field="projectId"
            header={t('project')}
            sortable
            headerClassName="table__header"
            body={(report) => getProjectName(report.projectId)}
            style={{ minWidth: '120px' }}
          />
          <Column
            field="type"
            header={t('type')}
            sortable
            headerClassName="table__header"
            body={(report: PeriodicReport) => (
              <div className="periodic-reports-page__type">
                {getTypeName(report.type)}
                {isLegacyLookerReport(report) && <ColorTag color="grey">{t('lookerLegacyFormat')}</ColorTag>}
              </div>
            )}
            style={{ minWidth: '110px' }}
          />
          <Column
            header={t('lookerDatasets')}
            headerClassName="table__header"
            body={(report: PeriodicReport) => {
              const current = currentLookerParameters(report);
              return current ? (
                // Dwa szare tagi w jednej linii, reszta pod „+N” z tooltipem.
                <div className="periodic-reports-page__datasets">
                  {keywordsTemplate(current.datasets.map(getDatasetName), 2, 'color-tag color-tag--grey', '')}
                </div>
              ) : (
                '-'
              );
            }}
            style={{ minWidth: '160px' }}
          />
          <Column
            header={t('lookerBuildState')}
            headerClassName="table__header"
            body={(report: PeriodicReport) => {
              const current = currentLookerParameters(report);
              if (!current) return '-';
              const { state, completedPeriods, totalPeriods } = current.build;
              return (
                <ColorTag color={getBuildStateColor(state)}>
                  {getBuildStateName(state)}
                  {state === 'BUILDING' && totalPeriods > 0 ? ` ${completedPeriods}/${totalPeriods}` : ''}
                </ColorTag>
              );
            }}
            style={{ minWidth: '100px' }}
          />
          <Column
            header={t('lookerHealth')}
            headerClassName="table__header"
            body={(report: PeriodicReport) => {
              const current = currentLookerParameters(report);
              if (!current) return '-';
              const tooltipId = `looker-health-${report.id}`;
              return (
                <>
                  <span id={tooltipId} className="periodic-reports-page__health">
                    <ColorTag color={getHealthColor(current.health.state)}>{getHealthName(current.health.state)}</ColorTag>
                  </span>
                  {current.health.lastErrorMessage && (
                    <Tooltip target={`#${tooltipId}`} content={current.health.lastErrorMessage} position="top" />
                  )}
                </>
              );
            }}
            style={{ minWidth: '110px' }}
          />

          <Column
            field="status"
            header={t('status')}
            sortable
            headerClassName="table__header"
            body={(report) => (
              <ColorTag color={getStatusColor(report.status)}>
                {getStatusName(report.status, report.provider)}
              </ColorTag>
            )}
            style={{ minWidth: '80px' }}
          />
          <Column
            field="lastExecuted"
            header={t('lastRefresh')}
            sortable
            headerClassName="table__header"
            bodyClassName="periodic-reports-page__date"
            body={(report: PeriodicReport) => {
              const refreshedAt = currentLookerParameters(report)?.lastRefreshAt ?? report.lastExecuted;
              return refreshedAt ? format(new Date(refreshedAt), 'yyyy-MM-dd / HH:mm') : t('never');
            }}
            style={{ minWidth: '120px' }}
          />
          <Column
            field="created"
            header={t('created')}
            sortable
            headerClassName="table__header"
            bodyClassName="periodic-reports-page__date"
            body={(report) => format(new Date(report.created), 'yyyy-MM-dd / HH:mm')}
            style={{ minWidth: '100px' }}
          />
          <Column
            header={t('action')}
            body={(report) => (
              <MoreBtn children={actionBodyTemplate(report)} rowId={report.id} />
            )}
            frozen
            alignFrozen="right"
            headerClassName="table__header table__header--center"
            bodyClassName="table__cell--gap table__cell--stack-actions"
            align="center"
            style={{ width: '34px' }}
          />
                </DataTable>
                </SimpleBar>
                  {/* Paginator poza SimpleBarem, w ramce tabeli — jak na liście projektów i w zespole. */}
                  <Paginator
                    className="pagination"
                    first={pageFirst}
                    rows={rows}
                    totalRecords={sortedReports.length}
                    rowsPerPageOptions={[10, 25, 50]}
                    currentPageReportTemplate={t('rowsNumber') + ':'}
                    template={
                      isMobile
                        ? MOBILE_PAGINATOR_TEMPLATE
                        : 'FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
                    }
                    pageLinkSize={isMobile ? 3 : 5}
                    onPageChange={(e) => {
                      setFirst(e.first);
                      setRows(e.rows);
                    }}
                  />
                </div>
              </div>
            ) : (
              <div className="empty-state-page periodic-reports-page__empty">
                <EmptyState
                  emptyStateData={{
                    title: t('noPeriodicReportsYet'),
                    imageClassName: 'img--project',
                    children: createReportButton,
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog className="dialog-box" />
      <ReportSetupDialog
        visible={setup.visible}
        report={setup.report}
        onHide={hideSetupDialog}
        onReportCreated={(report) => setSetup({ visible: true, report })}
        onLinkSaved={hideSetupDialog}
      />
    </div>
  );
};

export default PeriodicReportsPage;
